/* ===========================================================================
 *  De regels van het puntensysteem, als losse functies
 *
 *  Dit bestand wordt gedeeld: de functie (Deno) en de app (browser) lezen
 *  het allebei, en de toetsen (Node) ook. Daarom staat hier niets dat een
 *  database, een netwerk of een omgeving nodig heeft -- alleen rekenwerk.
 *
 *  De GETALLEN (punten per wasbeurt, cooldown, maximaal foute PIN's) staan
 *  niet hier maar in spec/rules.json, en via scripts/inrichten.ts in de
 *  database (collectie instellingen). Hier staat alleen hoe je ermee rekent.
 * =========================================================================== */

export type Dag = 'ma' | 'di' | 'wo' | 'do' | 'vr' | 'za' | 'zo'
export const DAGEN: Dag[] = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo']
export const DAGNAAM: Record<Dag, string> = {
  ma: 'maandag', di: 'dinsdag', wo: 'woensdag', do: 'donderdag', vr: 'vrijdag', za: 'zaterdag', zo: 'zondag',
}

export interface Tijdvak { van: string; tot: string }
/** Per dag een tijdvak, of null als hij die dag dicht is. */
export type Uren = Partial<Record<Dag, Tijdvak | null>>

/* ------------------------------------------------------------------ *
 *  Tijd in Nederland
 *
 *  De server draait in UTC en een tablet kan een verkeerde tijdzone hebben.
 *  "Nu open" gaat over de klok op de vestiging, dus altijd Europe/Amsterdam.
 * ------------------------------------------------------------------ */

const WEEKDAG: Record<string, Dag> = { Mon: 'ma', Tue: 'di', Wed: 'wo', Thu: 'do', Fri: 'vr', Sat: 'za', Sun: 'zo' }

export function amsterdam(d: Date): { dag: Dag; minuten: number; datum: string } {
  const delen = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Amsterdam', weekday: 'short', hour: '2-digit', minute: '2-digit',
    year: 'numeric', month: '2-digit', day: '2-digit', hourCycle: 'h23',
  }).formatToParts(d)
  const deel = (t: string) => delen.find((p) => p.type === t)?.value ?? ''
  return {
    dag: WEEKDAG[deel('weekday')] ?? 'ma',
    minuten: Number(deel('hour')) * 60 + Number(deel('minute')),
    datum: `${deel('year')}-${deel('month')}-${deel('day')}`,
  }
}

/** Middernacht in Nederland van een datum "2026-10-13", als tijdstip. */
export function amsterdamMiddernacht(datum: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datum)
  if (!m) throw new Error('Geen datum: ' + datum)
  const utc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  /* Hoeveel Nederland op dat moment voorloopt op UTC (60 of 120 minuten). */
  const naam = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Amsterdam', timeZoneName: 'shortOffset' })
    .formatToParts(new Date(utc)).find((p) => p.type === 'timeZoneName')?.value ?? 'GMT+1'
  const o = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(naam)
  const voor = o ? (o[1] === '-' ? -1 : 1) * (Number(o[2]) * 60 + Number(o[3] ?? 0)) : 60
  return new Date(utc - voor * 60_000)
}

const minuten = (hhmm: string): number => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm)
  return m ? Number(m[1]) * 60 + Number(m[2]) : NaN
}

export interface OpenStand {
  open: boolean
  /** Open: tot hoe laat vandaag. */
  tot?: string
  /** Dicht: wanneer de volgende opening is. */
  volgende?: { dag: Dag; van: string; vandaag: boolean; morgen: boolean }
  /** Er zijn helemaal geen openingstijden bekend. */
  onbekend?: boolean
}

export function openStand(uren: Uren | null | undefined, d: Date): OpenStand {
  if (!uren || !DAGEN.some((k) => uren[k])) return { open: false, onbekend: true }
  const nu = amsterdam(d)
  const vandaag = uren[nu.dag]
  if (vandaag) {
    const van = minuten(vandaag.van)
    const tot = minuten(vandaag.tot)
    if (nu.minuten >= van && nu.minuten < tot) return { open: true, tot: vandaag.tot }
    if (nu.minuten < van) return { open: false, volgende: { dag: nu.dag, van: vandaag.van, vandaag: true, morgen: false } }
  }
  const i = DAGEN.indexOf(nu.dag)
  for (let stap = 1; stap <= 7; stap++) {
    const dag = DAGEN[(i + stap) % 7] as Dag
    const vak = uren[dag]
    if (vak) return { open: false, volgende: { dag, van: vak.van, vandaag: false, morgen: stap === 1 } }
  }
  return { open: false, onbekend: true }
}

/** "Ma–Wo 07:00–19:00" — dagen met dezelfde tijden samen, voor op een kaartje. */
export function urenSamengevat(uren: Uren | null | undefined): { dagen: string; tijd: string }[] {
  if (!uren) return []
  const uit: { van: Dag; tot: Dag; tijd: string }[] = []
  for (const dag of DAGEN) {
    const vak = uren[dag]
    const tijd = vak ? `${vak.van}–${vak.tot}` : 'gesloten'
    const vorige = uit[uit.length - 1]
    if (vorige && vorige.tijd === tijd) vorige.tot = dag
    else uit.push({ van: dag, tot: dag, tijd })
  }
  const kort = (d: Dag) => d.charAt(0).toUpperCase() + d.slice(1)
  return uit.map((r) => ({ dagen: r.van === r.tot ? kort(r.van) : `${kort(r.van)}–${kort(r.tot)}`, tijd: r.tijd }))
}

/* ------------------------------------------------------------------ *
 *  Slugs: het adres van de tablet (/roosendaal)
 * ------------------------------------------------------------------ */

/** Paden die van de app zelf zijn en dus nooit een vestiging kunnen zijn. */
export const GERESERVEERD = new Set([
  'beheer', 'api', 'assets', 'manifest', 'afmelden', 'afbeelding', 'locaties', 'favicon.svg', 'favicon.ico', 'robots.txt', 'icoon', 'sw.js',
])

export function maakSlug(naam: string): string {
  return naam
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/^truckwash\s*1?\s*/, '')
    .replace(/[^a-z0-9]+/g, '')
    .slice(0, 40)
}

export function geldigeSlug(slug: string): boolean {
  return /^[a-z0-9]{2,40}$/.test(slug) && !GERESERVEERD.has(slug)
}

/* ------------------------------------------------------------------ *
 *  De code op de tag
 *
 *  Er zijn meerdere soorten fysieke codes in omloop (CRM-export, 13 okt
 *  2026, 29.593 codes):
 *
 *    C + 6 tekens       C3R3NL9        20.690   sinds 2022
 *    8 tekens           AHRZ7268        4.373   vooral 2023
 *    10 tekens          U2JFFIXDPY      3.321   vooral 2025
 *    11-13 cijfers      123456789012      627   2022 (streepjescode)
 *    met streepje       AB-CDEF1G           7
 *    een link           https://...?qr=AHRZ7268   11
 *
 *  Dus: elke reeks van 6 tot 24 letters en cijfers is een code. Spaties,
 *  streepjes en punten vallen weg; hoofd- en kleine letters zijn gelijk (in
 *  de export botst daardoor niets). Uit een link halen we de code alleen uit
 *  qr=, code=, r= enz. Een e-mailadres is nooit een code.
 *
 *  Wat er hier uitkomt, is ook de sleutel in de database: de import haalt
 *  de bestaande codes door DEZE functie, zodat een scan en de import altijd
 *  dezelfde vorm opleveren.
 * ------------------------------------------------------------------ */

const CODE = /^[A-Z0-9]{6,24}$/
const LINK_SLEUTELS = ['qr', 'code', 'tag', 'c', 'r', 'id', 'card', 'kaart']

const kaal = (s: string) => s.toUpperCase().replace(/[\s._/-]+/g, '')

export function normaliseerCode(invoer: unknown): string | null {
  if (typeof invoer !== 'string') return null
  const t = invoer.trim()
  if (!t || t.length > 500) return null

  /* Een link: de code staat in de query of in het laatste stuk van het pad. */
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(t)) {
    let u: URL
    try { u = new URL(t) } catch { return null }
    const param = new Map([...u.searchParams].map(([k, v]) => [k.toLowerCase(), v]))
    for (const k of LINK_SLEUTELS) {
      const w = param.get(k)
      if (w && CODE.test(kaal(w))) return kaal(w)
    }
    /* NIET het laatste stuk van het pad: in de export staan links naar een
       Google-review of een registratiepagina, en "REGISTER" zou dan de code
       van een klant worden die iedereen kan typen. */
    return null
  }

  if (t.includes('@')) return null
  const k = kaal(t)
  return CODE.test(k) ? k : null
}

/**
 * Dezelfde code met de tekens die op een kaart op elkaar lijken gelijkgetrokken.
 *
 * Ongeveer 8.200 bestaande codes hebben een O of 0, een I of 1. Een klant die
 * typt, haalt die door elkaar. We zoeken EERST op de code precies zoals hij
 * is; alleen als die niet bestaat op deze vorm -- en alleen als er dan
 * precies één tag bij past.
 */
export function codeVorm(code: string): string {
  return code.replace(/O/g, '0').replace(/[IL]/g, '1')
}

/* ------------------------------------------------------------------ *
 *  Punten, cooldown, PIN
 * ------------------------------------------------------------------ */

/** Hoeveel hele minuten nog te wachten, 0 als het weer mag. */
export function wachtMinuten(laatste: Date | null | undefined, nu: Date, cooldownMinuten: number): number {
  if (!laatste) return 0
  const over = laatste.getTime() + cooldownMinuten * 60_000 - nu.getTime()
  return over > 0 ? Math.ceil(over / 60_000) : 0
}

/** Hoeveel verschillende vestigingen nodig zijn voor de chauffeursbeloning. */
export function drempelAantal(procent: number, vestigingen: number): number {
  if (vestigingen <= 0) return 0
  return Math.min(vestigingen, Math.ceil((procent / 100) * vestigingen - 1e-9))
}

export function geldigePin(pin: unknown, lengte = 4): pin is string {
  return typeof pin === 'string' && new RegExp(`^\\d{${lengte}}$`).test(pin)
}

/** Euro-waarde van punten, voor op het scherm. */
export function euro(bedrag: number): string {
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', minimumFractionDigits: bedrag % 1 ? 2 : 0 }).format(bedrag)
}

/* ------------------------------------------------------------------ *
 *  Contactgegevens
 * ------------------------------------------------------------------ */

export function geldigEmail(e: unknown): e is string {
  return typeof e === 'string' && e.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e)
}

/** Een Nederlands nummer naar +31; iets anders blijft zoals het is (schoon). */
export function normaliseerMobiel(t: unknown): string {
  if (typeof t !== 'string') return ''
  const s = t.replace(/[^\d+]/g, '')
  if (/^06\d{8}$/.test(s)) return '+31' + s.slice(1)
  if (/^0031\d{9}$/.test(s)) return '+' + s.slice(2)
  if (/^\+31\d{9}$/.test(s)) return s
  return s
}

/** c****@gmail.com — genoeg om je eigen adres te herkennen, niet om het te oogsten. */
export function maskeerEmail(e: string | null | undefined): string | null {
  if (!e) return null
  const [naam, domein] = e.split('@')
  if (!naam || !domein) return null
  return `${naam.charAt(0)}${'•'.repeat(Math.min(Math.max(naam.length - 1, 2), 6))}@${domein}`
}

/* ------------------------------------------------------------------ *
 *  Verjaardag: alleen dag en maand
 * ------------------------------------------------------------------ */

const DAGEN_PER_MAAND = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
export const MAANDNAAM = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december']

export function geldigeVerjaardag(v: unknown): v is { dag: number; maand: number } {
  if (!v || typeof v !== 'object') return false
  const { dag, maand } = v as { dag?: unknown; maand?: unknown }
  return Number.isInteger(dag) && Number.isInteger(maand) && (maand as number) >= 1 && (maand as number) <= 12
    && (dag as number) >= 1 && (dag as number) <= (DAGEN_PER_MAAND[(maand as number) - 1] ?? 0)
}

/** Is het vandaag (in Nederland) de verjaardag? 29 februari valt in een gewoon jaar op 28 februari. */
export function isJarig(v: { dag: number; maand: number }, d: Date): boolean {
  const [j, m, dg] = amsterdam(d).datum.split('-').map(Number) as [number, number, number]
  if (v.maand === m && v.dag === dg) return true
  const schrikkel = (j % 4 === 0 && j % 100 !== 0) || j % 400 === 0
  return v.maand === 2 && v.dag === 29 && !schrikkel && m === 2 && dg === 28
}

/* ------------------------------------------------------------------ *
 *  Rollen (docs/rollen.md)
 * ------------------------------------------------------------------ */

export type Rol = 'medewerker' | 'locatiemanager' | 'om' | 'mt' | 'marketing'
export const ROLLEN: Rol[] = ['medewerker', 'locatiemanager', 'om', 'mt', 'marketing']
export const ROLNAAM: Record<Rol, string> = {
  medewerker: 'Medewerker',
  locatiemanager: 'Locatiemanager',
  om: 'Operationeel manager',
  mt: 'Management (MT)',
  marketing: 'Marketing',
}

/** Kan inloggen op /beheer (met e-mail en wachtwoord). */
export const kanInloggen = (r: Rol) => r !== 'medewerker'
/** Heeft een PIN om af te schrijven op de tablet. */
export const heeftPin = (r: Rol) => r !== 'marketing'
/** Werkt op alle vestigingen, ongeacht wat er is toegewezen. */
export const alleVestigingen = (r: Rol) => r === 'om' || r === 'mt'

export type Recht =
  | 'overzicht' | 'logboek' | 'klanten' | 'personen' | 'vestigingen' | 'tablets'
  | 'beloningen' | 'instellingen' | 'export-klanten' | 'export-marketing'
  | 'export-activiteit' | 'exportlog' | 'chauffeurs' | 'mails' | 'vouchers'

const RECHTEN: Record<Rol, Recht[]> = {
  medewerker: [],
  locatiemanager: ['overzicht', 'logboek', 'tablets'],
  om: ['overzicht', 'logboek', 'klanten', 'personen', 'vestigingen', 'tablets', 'beloningen', 'chauffeurs', 'vouchers'],
  mt: ['overzicht', 'logboek', 'klanten', 'personen', 'vestigingen', 'tablets', 'beloningen', 'instellingen',
    'export-klanten', 'export-marketing', 'export-activiteit', 'exportlog', 'chauffeurs', 'mails', 'vouchers'],
  marketing: ['mails', 'vouchers'],
}

export const mag = (r: Rol, recht: Recht) => RECHTEN[r].includes(recht)

/** Welke rollen iemand met rol `r` mag aanmaken of wijzigen. */
export function magRolBeheren(r: Rol, doel: Rol): boolean {
  if (r === 'mt') return true
  if (r === 'om') return doel === 'medewerker' || doel === 'locatiemanager'
  return false
}
